// src/crm/pipeline/PipelinePage.tsx
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAppStore } from '../../shell/appStore'
import { useDeals, usePatchDealStage, useCreateDeal } from '../hooks/useDeals'
import { useCampaigns } from '../hooks/useCampaigns'
import { useCreators } from '../api'
import { CONTACT_TYPE_LABELS } from '../components/ContactTypeBadge'
import { PipelineBoard } from './PipelineBoard'
import { MASKED_STAGES_BY_TYPE, STAGE_LABELS_BY_TYPE } from './stages'
import type { DealStage, Deal, CompModel, ContactType } from '../../lib/types'

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--elevated)',
  border: '1px solid var(--line-strong)',
  borderRadius: '6px',
  padding: '0.4375rem 0.625rem',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  outline: 'none',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'var(--muted)',
  marginBottom: '0.25rem',
}

export function PipelinePage() {
  const appId = useAppStore((s) => s.activeAppId)
  const queryClient = useQueryClient()
  const { data: deals, isLoading, isError } = useDeals(appId)
  const patchStage = usePatchDealStage(appId)
  const createDeal = useCreateDeal(queryClient)
  const { data: creators = [] } = useCreators()
  const { data: campaigns = [] } = useCampaigns(appId)

  const [showForm, setShowForm] = useState(false)
  const [creatorId, setCreatorId] = useState('')
  const [compModel, setCompModel] = useState<CompModel | ''>('')
  const [amount, setAmount] = useState('')
  const [campaignId, setCampaignId] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<ContactType | ''>('')

  if (!appId) {
    return (
      <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
        Sélectionnez une app pour voir le pipeline.
      </div>
    )
  }

  const activeAppId: string = appId

  function handleStageChange(dealId: string, stage: DealStage) {
    patchStage.mutate({ id: dealId, stage })
  }

  function resetForm() {
    setCreatorId('')
    setCompModel('')
    setAmount('')
    setCampaignId('')
    setFormError(null)
    setShowForm(false)
  }

  function handleDealSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!creatorId) {
      setFormError('Sélectionnez un créateur.')
      return
    }
    setFormError(null)
    createDeal.mutate(
      {
        creatorId,
        appId: activeAppId,
        stage: 'prospecte',
        compModel: compModel || null,
        amount: amount !== '' ? Number(amount) : null,
        currency: 'EUR',
        campaignId: campaignId || null,
      },
      {
        onSuccess: () => {
          resetForm()
        },
        onError: (err) => {
          setFormError(err instanceof Error ? err.message : 'Erreur inattendue')
        },
      },
    )
  }

  const showAmount = compModel === 'flat' || compModel === 'hybrid'

  // Filtre par type de contact : les deals portent creator_id, le type vit sur le contact.
  const contactTypeById = new Map(creators.map((c) => [c.id, c.contactType]))
  const visibleDeals = typeFilter
    ? (deals ?? []).filter((d) => contactTypeById.get(d.creatorId) === typeFilter)
    : (deals ?? [])

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem' }}>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--fg)', flex: 1 }}>
          Pipeline
        </h1>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            style={{
              background: 'var(--arancio)',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '0.4375rem 1rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            Nouveau deal
          </button>
        )}
        {patchStage.isError && (
          <span style={{ fontSize: '0.8125rem', color: 'var(--arancio)' }}>
            Échec du déplacement — état restauré.
          </span>
        )}
      </div>

      {showForm && (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '10px',
          padding: '1.5rem',
          maxWidth: '540px',
          marginBottom: '1.5rem',
        }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--fg)', marginBottom: '1.25rem', marginTop: 0 }}>
            Nouveau deal
          </h2>
          <form
            onSubmit={(e) => { void handleDealSubmit(e) }}
            style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}
          >
            {formError && (
              <div style={{
                background: 'rgba(217,83,30,0.1)',
                border: '1px solid rgba(217,83,30,0.25)',
                borderRadius: '6px',
                padding: '0.5rem 0.75rem',
                color: 'var(--arancio)',
                fontSize: '0.875rem',
              }}>
                {formError}
              </div>
            )}

            <div>
              <label htmlFor="deal-creator" style={labelStyle}>
                Créateur <span style={{ color: 'var(--arancio)' }}>*</span>
              </label>
              <select
                id="deal-creator"
                value={creatorId}
                onChange={(e) => setCreatorId(e.target.value)}
                style={inputStyle}
                required
              >
                <option value="">— Sélectionner un créateur —</option>
                {creators.map((c) => (
                  <option key={c.id} value={c.id}>{c.displayName}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="deal-model" style={labelStyle}>Modèle de rémunération</label>
              <select
                id="deal-model"
                value={compModel}
                onChange={(e) => setCompModel(e.target.value as CompModel | '')}
                style={inputStyle}
              >
                <option value="">— Aucun —</option>
                <option value="flat">Forfait fixe</option>
                <option value="gifting">Gifting</option>
                <option value="promo_affiliate">Code promo / Affiliation</option>
                <option value="hybrid">Hybride</option>
              </select>
            </div>

            {showAmount && (
              <div>
                <label htmlFor="deal-amount" style={labelStyle}>Montant (€)</label>
                <input
                  id="deal-amount"
                  type="number"
                  min={0}
                  step={0.01}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  style={inputStyle}
                />
              </div>
            )}

            <div>
              <label htmlFor="deal-campaign" style={labelStyle}>Campagne</label>
              <select
                id="deal-campaign"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                style={inputStyle}
              >
                <option value="">— Aucune —</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: '0.4375rem 1rem',
                  background: 'transparent',
                  border: '1px solid var(--line)',
                  borderRadius: '6px',
                  color: 'var(--muted)',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={createDeal.isPending}
                style={{
                  padding: '0.4375rem 1rem',
                  background: 'var(--arancio)',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: createDeal.isPending ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  opacity: createDeal.isPending ? 0.7 : 1,
                }}
              >
                {createDeal.isPending ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <select
          aria-label="Filtrer par type de contact"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as ContactType | '')}
          style={{
            background: 'var(--elevated)',
            border: '1px solid var(--line-strong)',
            borderRadius: '7px',
            padding: '0.4375rem 0.75rem',
            color: 'var(--fg)',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
          }}
        >
          <option value="">Tous les types</option>
          {Object.entries(CONTACT_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Chargement…</div>
      ) : isError ? (
        <div style={{ color: 'var(--arancio)', fontSize: '0.875rem' }}>
          Erreur lors du chargement des deals.
        </div>
      ) : (
        <PipelineBoard
          deals={enrichDealsWithCreatorName(visibleDeals, creators)}
          {...(typeFilter
            ? {
                labels: STAGE_LABELS_BY_TYPE[typeFilter],
                hiddenStages: MASKED_STAGES_BY_TYPE[typeFilter],
              }
            : {})}
          onStageChange={handleStageChange}
          isPatchPending={patchStage.isPending}
        />
      )}
    </div>
  )
}

// Les deals ont creator_id ; le nom vient du champ creatorName enrichi par le groupe API (Deal étendu).
// Si absent (Deal basique), on affiche le creator_id tronqué.
function enrichDealsWithCreatorName(
  deals: Deal[],
  creators: { id: string; displayName: string }[],
): (Deal & { creatorName?: string })[] {
  return deals.map((d) => {
    const creator = creators.find((c) => c.id === d.creatorId)
    return {
      ...d,
      creatorName:
        (d as Deal & { creatorName?: string }).creatorName ??
        creator?.displayName ??
        d.creatorId.slice(0, 8),
    }
  })
}
